/**
 * Admin Reward Claims dashboard
 *
 * Two halves:
 *   - Top: stats summary (last 30 days)
 *   - Bottom: paginated claims feed with flag / clawback actions
 *
 * Filters: flagged-only, clawed-only, by reward type, by user.
 */

import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { Loader2, Flag, RotateCcw, AlertTriangle, ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { authFetch } from '@/lib/auth-client';

interface DashboardStats {
  windowDays: number;
  byCategory: Array<{ category: string; claims: number; coinsAwarded: number; coinsClawed: number }>;
  byRewardType: Array<{ rewardType: string; claims: number; coinsAwarded: number; coinsClawed: number }>;
  topEarners: Array<{ userId: string; username: string; totalCoins: number; claimCount: number }>;
  flagged: number;
  clawedBack: number;
  totalCoinsAwarded: number;
  totalCoinsClawed: number;
}

interface Claim {
  id: string;
  userId: string;
  rewardType: string;
  coinsAwarded: number;
  flagged: boolean;
  flaggedReason: string | null;
  clawedBackAt: string | null;
  clawbackReason: string | null;
  ipAddress: string | null;
  deviceId: string | null;
  verificationData: Record<string, unknown> | null;
  createdAt: string;
  user: { id: string; username: string };
}

export default function AdminRewardClaimsPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const [claims, setClaims] = useState<Claim[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [claimsLoading, setClaimsLoading] = useState(true);
  const [filter, setFilter] = useState<{ flagged: boolean; clawedBack: boolean | null }>({ flagged: false, clawedBack: null });

  const [error, setError] = useState<string | null>(null);
  const [busyClaimId, setBusyClaimId] = useState<string | null>(null);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await authFetch('/api/admin/reward-stats?windowDays=30');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setStats((body.data ?? body) as DashboardStats);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load stats');
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const loadClaims = useCallback(async (cursor?: string | null) => {
    setClaimsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.flagged) params.set('flagged', 'true');
      if (filter.clawedBack !== null) params.set('clawedBack', String(filter.clawedBack));
      if (cursor) params.set('cursor', cursor);
      params.set('limit', '50');

      const res = await authFetch(`/api/admin/reward-claims?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      const data = body.data ?? body;
      setClaims(cursor ? (prev) => [...prev, ...(data.items as Claim[])] as Claim[] : (data.items as Claim[]));
      setNextCursor(data.nextCursor ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load claims');
    } finally {
      setClaimsLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    void loadStats();
  }, [loadStats]);

  useEffect(() => {
    void loadClaims(null);
  }, [loadClaims]);

  const handleFlag = async (claim: Claim) => {
    const reason = prompt('Reason for flagging:');
    if (!reason || !reason.trim()) return;
    setBusyClaimId(claim.id);
    try {
      await authFetch(`/api/admin/reward-claims/${claim.id}/flag`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason.trim() }),
      });
      await Promise.all([loadStats(), loadClaims(null)]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Flag failed');
    } finally {
      setBusyClaimId(null);
    }
  };

  const handleUnflag = async (claim: Claim) => {
    setBusyClaimId(claim.id);
    try {
      await authFetch(`/api/admin/reward-claims/${claim.id}/flag`, { method: 'DELETE' });
      await Promise.all([loadStats(), loadClaims(null)]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unflag failed');
    } finally {
      setBusyClaimId(null);
    }
  };

  const handleClawback = async (claim: Claim) => {
    const reason = prompt(`Clawback ${claim.coinsAwarded}c from @${claim.user.username}? Enter reason:`);
    if (!reason || !reason.trim()) return;
    if (!confirm(`Confirm clawback of ${claim.coinsAwarded}c from @${claim.user.username}.`)) return;
    setBusyClaimId(claim.id);
    try {
      const res = await authFetch(`/api/admin/reward-claims/${claim.id}/clawback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason.trim() }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      await Promise.all([loadStats(), loadClaims(null)]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Clawback failed');
    } finally {
      setBusyClaimId(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-4">
      <Link href="/admin/rewards" className="inline-flex items-center gap-1 text-xs text-warm-gray/60 hover:text-cream">
        <ArrowLeft className="w-3 h-3" /> Back to Rewards
      </Link>

      <div>
        <h1 className="text-xl font-bold text-cream">Reward Claims</h1>
        <p className="text-xs text-warm-gray/50 mt-0.5">Audit feed + flag / clawback for any payout.</p>
      </div>

      {error && (
        <div className="rounded-lg bg-oxblood/10 border border-oxblood/30 text-oxblood text-xs p-3 flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5" /> {error}
          <button onClick={() => setError(null)} className="ml-auto text-warm-gray/60 hover:text-cream">×</button>
        </div>
      )}

      {/* Stats */}
      {statsLoading ? (
        <div className="text-xs text-warm-gray/50 inline-flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Loading stats…</div>
      ) : stats ? (
        <div className="space-y-3">
          <div className="grid grid-cols-4 gap-2">
            <Stat label={`Coins paid (${stats.windowDays}d)`} value={`${stats.totalCoinsAwarded.toLocaleString()}c`} />
            <Stat label="Coins clawed back" value={`${stats.totalCoinsClawed.toLocaleString()}c`} mood={stats.totalCoinsClawed > 0 ? 'warn' : 'ok'} />
            <Stat label="Flagged (open)" value={stats.flagged} mood={stats.flagged > 0 ? 'warn' : 'ok'} />
            <Stat label="Clawed back (lifetime)" value={stats.clawedBack} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-surface-3 bg-surface-2 p-3">
              <div className="text-xs font-bold text-cream mb-2">By category ({stats.windowDays}d)</div>
              <div className="space-y-1">
                {stats.byCategory.map((c) => (
                  <div key={c.category} className="flex items-center justify-between text-xs">
                    <span className="text-warm-gray/70">{c.category}</span>
                    <span className="text-gold font-bold">{c.coinsAwarded.toLocaleString()}c</span>
                  </div>
                ))}
                {stats.byCategory.length === 0 && <div className="text-[10px] text-warm-gray/40 italic">No claims in window.</div>}
              </div>
            </div>

            <div className="rounded-lg border border-surface-3 bg-surface-2 p-3">
              <div className="text-xs font-bold text-cream mb-2">Top earners ({stats.windowDays}d)</div>
              <div className="space-y-1">
                {stats.topEarners.map((u) => (
                  <div key={u.userId} className="flex items-center justify-between text-xs">
                    <span className="text-warm-gray/70 truncate">@{u.username}</span>
                    <span className="text-gold font-bold">{u.totalCoins.toLocaleString()}c</span>
                  </div>
                ))}
                {stats.topEarners.length === 0 && <div className="text-[10px] text-warm-gray/40 italic">No claims in window.</div>}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Filter pills */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <FilterPill active={!filter.flagged && filter.clawedBack === null} onClick={() => setFilter({ flagged: false, clawedBack: null })}>All</FilterPill>
        <FilterPill active={filter.flagged} onClick={() => setFilter({ flagged: true, clawedBack: null })}>Flagged</FilterPill>
        <FilterPill active={filter.clawedBack === true} onClick={() => setFilter({ flagged: false, clawedBack: true })}>Clawed back</FilterPill>
        <FilterPill active={filter.clawedBack === false} onClick={() => setFilter({ flagged: false, clawedBack: false })}>Active payouts</FilterPill>
      </div>

      {/* Claims feed */}
      <div className="rounded-lg border border-surface-3 bg-surface-2 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-surface-3/50 text-[10px] text-warm-gray/60 uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Time</th>
              <th className="text-left px-3 py-2">User</th>
              <th className="text-left px-3 py-2">Reward</th>
              <th className="text-right px-3 py-2">Coins</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Reason</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {claims.map((c) => {
              const isClawed = c.clawedBackAt !== null;
              return (
                <tr key={c.id} className={cn('border-t border-surface-3/50', isClawed && 'opacity-60')}>
                  <td className="px-3 py-2 text-warm-gray/60 whitespace-nowrap">{new Date(c.createdAt).toLocaleString()}</td>
                  <td className="px-3 py-2 text-cream">@{c.user.username}</td>
                  <td className="px-3 py-2 text-warm-gray/70 font-mono text-[10px]">{c.rewardType}</td>
                  <td className="px-3 py-2 text-right text-gold font-bold">{c.coinsAwarded}</td>
                  <td className="px-3 py-2">
                    {isClawed ? <Badge tone="oxblood">Clawed</Badge>
                      : c.flagged ? <Badge tone="warn">Flagged</Badge>
                      : <Badge tone="ok">Active</Badge>}
                  </td>
                  <td className="px-3 py-2 text-warm-gray/50 truncate max-w-xs">
                    {c.clawbackReason ?? c.flaggedReason ?? ''}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      {!isClawed && !c.flagged && (
                        <button onClick={() => handleFlag(c)} disabled={busyClaimId === c.id} className="p-1 rounded hover:bg-warning/20 text-warm-gray/70 hover:text-warning" title="Flag for review">
                          <Flag className="w-3 h-3" />
                        </button>
                      )}
                      {!isClawed && c.flagged && (
                        <button onClick={() => handleUnflag(c)} disabled={busyClaimId === c.id} className="p-1 rounded hover:bg-surface-4 text-warm-gray/70 hover:text-cream" title="Unflag">
                          <Flag className="w-3 h-3 line-through" />
                        </button>
                      )}
                      {!isClawed && (
                        <button onClick={() => handleClawback(c)} disabled={busyClaimId === c.id} className="p-1 rounded hover:bg-oxblood/20 text-warm-gray/70 hover:text-oxblood" title="Clawback">
                          <RotateCcw className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {claimsLoading && (
          <div className="px-3 py-4 text-xs text-warm-gray/50 inline-flex items-center gap-1.5">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading…
          </div>
        )}

        {!claimsLoading && claims.length === 0 && (
          <div className="px-3 py-6 text-xs text-warm-gray/40 text-center italic">No claims match the current filter.</div>
        )}

        {nextCursor && (
          <div className="border-t border-surface-3/50 p-2 text-center">
            <button
              onClick={() => loadClaims(nextCursor)}
              disabled={claimsLoading}
              className="px-3 py-1 rounded text-xs text-warm-gray/70 hover:text-cream"
            >
              Load more
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, mood = 'neutral' }: { label: string; value: string | number; mood?: 'neutral' | 'ok' | 'warn' }) {
  return (
    <div className={cn(
      'rounded-lg border bg-surface-2 px-3 py-2',
      mood === 'warn' ? 'border-warning/40' : 'border-surface-3',
    )}>
      <div className="text-[10px] text-warm-gray/50 uppercase tracking-wider">{label}</div>
      <div className={cn('text-base font-bold', mood === 'warn' ? 'text-warning' : 'text-cream')}>{value}</div>
    </div>
  );
}

function FilterPill({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-3 py-1 rounded-full text-[11px] font-semibold border transition-colors',
        active
          ? 'bg-gold/15 text-gold border-gold/30'
          : 'bg-surface-3 text-warm-gray/70 border-surface-4 hover:text-cream',
      )}
    >
      {children}
    </button>
  );
}

function Badge({ tone, children }: { tone: 'ok' | 'warn' | 'oxblood'; children: React.ReactNode }) {
  return (
    <span className={cn(
      'inline-block px-1.5 py-0.5 rounded text-[10px] font-bold',
      tone === 'ok' && 'bg-verdigris/15 text-verdigris border border-verdigris/30',
      tone === 'warn' && 'bg-warning/15 text-warning border border-warning/30',
      tone === 'oxblood' && 'bg-oxblood/15 text-oxblood border border-oxblood/30',
    )}>{children}</span>
  );
}
